import globals from "@lvt/globals";
import { PointFeature } from "supercluster";

import { MapFeatureData } from "@/common/components/MapLandingPage/Map/types";
import { isUnitActive } from "@/utilities/liveUnitHelper";
import { UnitType } from "@/utilities/types/liveunit";
import { LatLong, OptionalLatLng } from "@/utilities/types/map";
import { ArchiveCabinetSearchData, ResultTypes, SearchSourceType } from "@/utilities/types/search";


export const isValidLongitude = (longitude: number) => -180 <= longitude && longitude <= 180;

export const isValidLatitude = (latitude: number) => -90 <= latitude && latitude <= 90;

export const hasValidLatLong = (data: OptionalLatLng): data is LatLong => {
    return (
        typeof data.latitude === "number" &&
        typeof data.longitude === "number" &&
        isValidLatitude(data.latitude) &&
        isValidLongitude(data.longitude)
    );
};

export function searchToMapFeatureData(searches: Array<ResultTypes>): MapFeatureData[] {
    const mapData: MapFeatureData[] = [];
    const groupedArchiveCabinets: Record<string, Array<ArchiveCabinetSearchData>> = {};
    let invalidLiveUnits = 0;
    let invalidCabinets = 0;
    let groupedCabinets = 0;

    searches.forEach((unit: ResultTypes) => {
        if (unit.type === SearchSourceType.ArchiveCabinet) {
            if (hasValidLatLong(unit)) {
                const { latitude, longitude, name } = unit;

                const identifier = `${longitude.toString()}|${latitude.toString()}|${name}`;

                if (groupedArchiveCabinets[identifier] != null) {
                    groupedArchiveCabinets[identifier].push(unit);
                } else {
                    groupedArchiveCabinets[identifier] = [unit];
                }
            } else {
                invalidCabinets += 1;
            }
        }

        if (unit.type === SearchSourceType.LiveUnit) {
            if (hasValidLatLong(unit)) {
                const { id, name, serial, location_name, latitude, longitude, date_activated, date_deactivated } = unit;
                const type = isUnitActive(date_activated, date_deactivated) ? UnitType.LiveUnit : UnitType.Deactivated;

                const unitDisplayName = name.includes(serial) ? name : `${name}, ${serial}`;

                const mapDatum: MapFeatureData = {
                    geometry: {
                        type: "Point",
                        coordinates: [longitude, latitude],
                    },
                    properties: {
                        cluster: false,
                        id: id,
                        tooltip: `${location_name}, ${unitDisplayName}`,
                        unitType: type,
                    },
                    type: "Feature",
                };

                mapData.push(mapDatum);
            } else {
                invalidLiveUnits += 1;
            }
        }
    });

    const archiveCabinetsMapData = Object.values<Array<ArchiveCabinetSearchData>>(groupedArchiveCabinets).map(
        (cabinets: Array<ArchiveCabinetSearchData>): MapFeatureData => {
            // In the future we will want a way to access the different archive
            // cabinets at a particular location. For now, just showing a single
            // archive cabinet.
            const archiveCabinet = cabinets[0];

            groupedCabinets += cabinets.length - 1;

            return {
                geometry: {
                    type: "Point",
                    coordinates: [archiveCabinet.longitude as number, archiveCabinet.latitude as number],
                },
                properties: {
                    cluster: false,
                    id: archiveCabinet.id,
                    tooltip: `${archiveCabinet.location_name}, ${archiveCabinet.name}`,
                    unitType: UnitType.ArchiveCabinet,
                },
                type: "Feature",
            };
        },
    );

    if (globals.sentry.environment === "local") {
        console.log(
            `${groupedCabinets} cabinets not shown due to grouping`,
            "\n",
            `${invalidCabinets} cabinets not shown due to invalid coordinates`,
            "\n",
            `${invalidLiveUnits} live units not shown due to invalid coordinates`,
        );
    }

    return [...archiveCabinetsMapData, ...mapData];
}

export function boundsForPoints<P>(
    mapsApi: typeof google.maps,
    points: Array<PointFeature<P>>,
): google.maps.LatLngBounds {
    const bounds = new mapsApi.LatLngBounds();

    points.forEach((p) => {
        const [lng, lat] = p.geometry.coordinates;

        bounds.extend({ lat, lng });
    });

    const diffLng = bounds.getNorthEast().lng() - bounds.getSouthWest().lng();

    // Reset bounds that span the whole map to -180/180.
    if (-2 < diffLng && diffLng < 0) {
        const lat = bounds.getSouthWest().lat();

        bounds.union({ west: -180, east: 180, north: lat, south: lat });
    }

    return bounds;
}

export function mapFitBounds(
    map: google.maps.Map,
    bounds: google.maps.LatLngBounds,
    paddingPercentage: number,
    minimumZoom?: number,
    maximumZoom?: number,
) {
    const { width: mapWidth, height: mapHeight } = map.getDiv().getBoundingClientRect();
    const paddingWidth = mapWidth * paddingPercentage;
    const paddingHeight = mapHeight * paddingPercentage;
    const padding = {
        top: paddingHeight,
        right: paddingWidth,
        bottom: paddingHeight,
        left: paddingWidth,
    };

    map.setOptions({ minZoom: minimumZoom });
    map.setOptions({ maxZoom: maximumZoom });
    map.fitBounds(bounds, padding);
    map.setOptions({ minZoom: undefined });
    map.setOptions({ maxZoom: undefined });
}

// Zoom level for individual LiveUnit view
export const LIVE_UNIT_LEVEL_ZOOM = 17;

// This default map config will center on the United States.
export const DEFAULT_MAP_CONFIG = {
    center: { lat: 37.0902, lng: -95.7129 },
    zoom: 2,
};
